import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  REFERRAL_STATUSES,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
} from '../../constants/partnerNetwork';
import { PartnerReferral, listMyReferrals } from '../../services/partnerPortal';
import { formatDate, formatMoney } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Pagination,
  Spinner,
  StatusBadge,
  filterClass,
} from '../partner-network/ui';

/** The partner's own referrals. The server scopes this to them; nothing here can widen it. */
const PartnerReferrals: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [referrals, setReferrals] = useState<PartnerReferral[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === search) return;
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.delete('page');
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  const setFilter = (key: string, value: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listMyReferrals({
        status: status || undefined,
        search: search || undefined,
        page,
        limit: 25,
      });
      setReferrals(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your referrals');
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
                My referrals
              </h1>
              <p className="text-sm text-muted">
                Every business you have introduced, and where each one stands.
              </p>
            </div>
            <Link to="/partner/referrals/new" className="btn-primary py-2.5 px-5">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit referral
            </Link>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-card p-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search business or referral ID…"
            className={`${filterClass} min-w-[200px] flex-1`}
          />
          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {REFERRAL_STATUSES.map((value) => (
              <option key={value} value={value}>
                {REFERRAL_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-xl border border-surface-border bg-surface-card">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-10 w-10" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={status || search ? 'Nothing matches that' : 'No referrals yet'}
                description={
                  status || search
                    ? 'Try clearing the filters.'
                    : 'When you know a business that needs what we build, submit it here and we take it from there.'
                }
                action={
                  !status && !search ? (
                    <Link to="/partner/referrals/new" className="btn-primary py-2.5 px-5">
                      Submit your first referral
                    </Link>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Referral ID</th>
                    <th className="px-4 py-3 font-medium">Business</th>
                    <th className="px-4 py-3 font-medium">Submitted</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Value</th>
                    <th className="px-4 py-3 font-medium">Commission</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                    <th className="px-4 py-3 text-right font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr
                      key={referral.id}
                      className="border-b border-surface-border/60 transition-colors last:border-0 hover:bg-surface-hover/40"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/partner/referrals/${referral.id}`}
                          className="font-mono text-xs text-primary-400 hover:underline"
                        >
                          {referral.referralId}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="block font-medium text-white">
                          {referral.businessName}
                        </span>
                        <span className="block text-xs text-muted">
                          {referral.businessType || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatDate(referral.submittedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={REFERRAL_STATUS_LABELS[referral.status]}
                          tone={REFERRAL_STATUS_TONES[referral.status]}
                        />
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {referral.projectValue !== null
                          ? formatMoney(referral.projectValue)
                          : referral.estimatedValue !== null
                          ? `est. ${formatMoney(referral.estimatedValue)}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="block text-white">
                          {formatMoney(referral.potentialCommission)}
                        </span>
                        {referral.commissionStatus && (
                          <StatusBadge
                            className="mt-1"
                            label={COMMISSION_STATUS_LABELS[referral.commissionStatus]}
                            tone={COMMISSION_STATUS_TONES[referral.commissionStatus]}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatDate(referral.updatedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/partner/referrals/${referral.id}`}
                          className="rounded-lg px-3 py-1.5 text-xs text-muted transition-colors hover:text-white"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && referrals.length > 0 && (
            <div className="border-t border-surface-border px-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                onChange={(next) => setFilter('page', String(next))}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnerReferrals;
